import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CurrentDutyStatus, DutyLogService } from 'src/app/core/duty-log.service';
import { originatesFromGoldenHour } from 'src/store/duty-log/duty-log.model';
import { filterNull } from 'src/util/rxjs/operators';
import { UserViewModel } from 'src/view-models/user-view-model';
import { CurrentUserService } from 'src/app/core/current-user.service';
import { SwitchUserDialogComponent } from '../switch-user-dialog/switch-user-dialog.component';
import { AuthProxyService } from '../../authentication/auth-proxy.service';

@Component({
  selector: 'fl-default-layout',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
})
export class DefaultLayoutComponent implements OnInit {
  public currentDutyStatus$: Observable<CurrentDutyStatus>;
  public user$: Observable<UserViewModel>;
  public isImpersonating$: Observable<boolean>;
  public impersonatedByUser$: Observable<UserViewModel | undefined>;
  public canImpersonate$: Observable<boolean>;
  public title$: Observable<string>;

  constructor(
    private _user: CurrentUserService,
    private _dutyLogService: DutyLogService,
    private _dialog: MatDialog,
    private _authProxy: AuthProxyService,
  ) {
    this.currentDutyStatus$ = this._dutyLogService.currentDutyStatus$;
    this.canImpersonate$ = this._user.authenticated.valueChanges.pipe(
      map((user) => user?.canImpersonate === true),
    );
  }

  get canEditUnit$(): Observable<boolean> {
    return combineLatest([this.canEdityDutyLog$, this.user$, this.currentDutyStatus$]).pipe(
      map(
        ([canEditDutyLog, user, ds]) =>
          canEditDutyLog &&
          user.flightLogFeatures.tcsUnitManagement &&
          ds.base?.dispatchedByExternalCad !== true &&
          ds.base?.enableFlightLogDutyLog === true,
      ),
    );
  }

  get showDutyStatusButton$(): Observable<boolean> {
    return combineLatest([
      this._dutyLogService.currentDutyStatus$.pipe(startWith(undefined as any)),
      this.user$,
    ]).pipe(
      map(([ds, user]) => {
        if (ds != null) {
          if (
            // If the latest shift is active and originates from golden hour
            ds.dutyLog.dutyPeriodEndTime == null &&
            originatesFromGoldenHour(ds.dutyLog)
          ) {
            return false;
          }

          if (ds.base?.enableFlightLogDutyLog === true && ds.dutyLog.dutyPeriodEndTime == null) {
            // if base for the latest shift has duty log management enabled and it is active
            return true;
          }
        }

        return user.flightLogFeatures.dutyLogManagementForUser;
      }),
    );
  }

  get canEdityDutyLog$(): Observable<boolean> {
    // eslint-disable-next-line import/no-deprecated
    return combineLatest([this._dutyLogService.currentDutyStatus$, this.user$]).pipe(
      map(([ds, user]) => {
        if (originatesFromGoldenHour(ds.dutyLog)) {
          return false;
        }
        if (user.flightLogFeatures.dutyLogManagementForUser) {
          return true;
        }
        if (ds.base != null && ds.base.enableFlightLogDutyLog) {
          return true;
        }
        return false;
      }),
    );
  }

  public ngOnInit() {
    this.user$ = this._user.valueChanges.pipe(filterNull());
    this.impersonatedByUser$ = this._user.authenticated.valueChanges;
    this.isImpersonating$ = this._user.impersonated.valueChanges.pipe(map((imp) => imp != null));
    this.title$ = this.user$.pipe(
      map((user) =>
        user.isPrdUser && !user.isFlightLogUser ? 'Pilot Record Database' : 'Flight Log',
      ),
    );
  }

  public switchUser() {
    this._dialog.open<SwitchUserDialogComponent>(SwitchUserDialogComponent, {});
  }

  public async clearImpersonation() {
    await this._authProxy.stopImpersonating();
    window.location.reload();
  }
}
